/**
 * book-group-status-summary — 书组折叠行的「整组受众档」摘要。
 *
 * 一个书组由多条 per-locale 语言书组成，各自有独立的受众档（层② 覆盖层①，A6 §15.1 展示口径）。
 * 折叠态只给一个 chip，所以要把多条压成一句话：全同 → `全部X`；不同 → `档位不一`。
 *
 * **判色与标签一律取 `~/lib/novel-visibility-display`**（唯一口径）—— 本模块只做「多行压一行」，
 * 不自己判色、不自己写标签。四档化前这里各判了一套中文串比较，加档位时必漏。
 */

import type { NovelVisibility } from '@novel-admin/shared';
import { isAdminInternalLocale } from '~/lib/admin-internal-locale';
import {
  visibilityChipClass,
  visibilityDot,
  visibilityLabel,
} from '~/lib/novel-visibility-display';

const TITLE_BASE =
  '整组受众档摘要 · 由各语言书自己的受众档（层② 覆盖层①）汇总 · 只读，改档请到单本语言书';

/**
 * 压成一个 chip。**中文行不参与聚合**。
 *
 * `zh` / `zh-*` 是 admin 内部预览语言，C 端一个用户也看不到它的档位
 * （判据见 `~/lib/admin-internal-locale`）。把它聚进来，两条真投放语言都 `PUBLIC` 的书组会被一条
 * 隐藏的中文行冲成「档位不一」—— 那个提示读不出任何可行动信息，运营还得展开逐行核对，
 * 才发现「不一」的是一条根本不投放的行。
 *
 * 全组只有 admin 内部语言时**退回按全部行聚合**：那种书组的档位仍要如实显示，画成「未配置」
 * 会与「一条语言行都没有」混淆 —— 两者的处置完全不同。
 */
export function bookGroupStatusSummary(
  rows: Array<{ locale: string; visibility: NovelVisibility }>,
): {
  label: string;
  cls: string;
  dot: string;
  title: string;
} {
  const servable = rows.filter((r) => !isAdminInternalLocale(r.locale));
  const excludedCount = rows.length - servable.length;
  const effective = servable.length > 0 ? servable : rows;
  const title =
    servable.length > 0 && excludedCount > 0
      ? `${TITLE_BASE} · 已排除 ${excludedCount} 条中文行（admin 内部语言，不投放 C 端）`
      : TITLE_BASE;

  const uniq = [...new Set(effective.map((r) => r.visibility).filter(Boolean))];
  if (uniq.length === 0) return { label: '未配置', cls: '', dot: 'var(--fg-faint)', title };
  if (uniq.length === 1) {
    const v = uniq[0] as NovelVisibility;
    return {
      label: `全部${visibilityLabel(v)}`,
      cls: visibilityChipClass(v),
      dot: visibilityDot(v),
      title,
    };
  }
  return { label: '档位不一', cls: '', dot: 'var(--fg-faint)', title };
}
